// Sign-in for the SPA: OIDC Authorization Code + PKCE via oidc-client-ts.
//
// Provider-agnostic: the backend's public /api/auth/config says which identity
// provider to use (Cognito in UAT, Entra in HCC), so the same build runs on
// every stack. When the backend is not in jwt mode (local dev, or a stack still
// behind the ALB's own login) this module does nothing and requests go out
// without a token, exactly as before.
//
// Tokens live in sessionStorage: a reload keeps you signed in, closing the tab
// signs you out (agreed for shared ward machines).

import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

export const CALLBACK_PATH = '/auth/callback';

let manager = null;   // UserManager, only in jwt mode
let config = null;    // the /api/auth/config response
let renewing = null;  // in-flight token renewal, shared by concurrent callers
let initPromise = null;

const sessionStore = () => new WebStorageStateStore({ store: window.sessionStorage });

// Plain fetch on purpose: this endpoint is public and apiFetch depends on it.
async function loadConfig() {
  const res = await fetch('/api/auth/config', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`auth config: HTTP ${res.status}`);
  return res.json();
}

const currentLocation = () =>
  window.location.pathname + window.location.search + window.location.hash;

/**
 * Run once before the app renders. Resolves to:
 *   { status: 'off' }                     backend not in jwt mode; no sign-in
 *   { status: 'signed-in', user }         a valid session exists
 *   { status: 'redirecting' }             sent to the login page; render nothing
 */
export function initAuth() {
  // Once per page load: React StrictMode runs effects twice in development, and
  // a login code can only be exchanged once.
  if (!initPromise) initPromise = doInitAuth();
  return initPromise;
}

async function doInitAuth() {
  config = await loadConfig();
  if (config.mode !== 'jwt') return { status: 'off' };

  const origin = window.location.origin;
  manager = new UserManager({
    authority: config.authority,
    client_id: config.client_id,
    redirect_uri: origin + CALLBACK_PATH,
    post_logout_redirect_uri: origin + '/',
    response_type: 'code',
    scope: config.scope,
    userStore: sessionStore(),
    stateStore: sessionStore(),
    // Renew ahead of expiry using the refresh token, so a long solver poll
    // never runs into an expired access token.
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 120,
    loadUserInfo: false,
  });

  // Returning from the login page: exchange the code, then put the user back
  // where they started (the app keeps its tab and week in the URL hash).
  if (window.location.pathname === CALLBACK_PATH) {
    const user = await manager.signinRedirectCallback();
    const returnTo = (user.state && user.state.returnTo) || '/';
    window.history.replaceState(null, '', returnTo);
    return { status: 'signed-in', user };
  }

  let user = await manager.getUser();
  if (user && user.expired) user = await renewToken();
  if (user && !user.expired) return { status: 'signed-in', user };

  await signIn();
  return { status: 'redirecting' };
}

export const authEnabled = () => manager !== null;

/** Access token for the Authorization header, or null when auth is off. */
export async function getAccessToken() {
  if (!manager) return null;
  const user = await manager.getUser();
  if (user && !user.expired) return user.access_token;
  const renewed = await renewToken();
  return renewed ? renewed.access_token : null;
}

/** Try the refresh token once; concurrent callers share the same attempt. */
export function renewToken() {
  if (!manager) return Promise.resolve(null);
  if (!renewing) {
    renewing = manager.signinSilent()
      .catch(() => null)
      .finally(() => { renewing = null; });
  }
  return renewing;
}

/** Send the user to the provider's login page, remembering where they were. */
export function signIn() {
  if (!manager) return Promise.resolve();
  return manager.signinRedirect({ state: { returnTo: currentLocation() } });
}

/**
 * Sign out of the app AND the provider. Ending the provider session matters on
 * shared machines: otherwise the next person to click "sign in" would be let
 * straight back in as the previous user.
 */
export async function signOut() {
  if (!manager) return;
  const user = await manager.getUser();
  await manager.removeUser();
  if (config.logout_url) {
    // Cognito: its discovery document has no end_session_endpoint, so the
    // backend supplies the hosted-UI logout URL.
    window.location.assign(config.logout_url);
    return;
  }
  // Entra (and any provider that advertises end_session_endpoint).
  await manager.signoutRedirect({ id_token_hint: user ? user.id_token : undefined });
}

/** Signed-in user's display name, from the ID token claims. */
export async function currentUserName() {
  if (!manager) return null;
  const user = await manager.getUser();
  const p = (user && user.profile) || {};
  return p.name || p.preferred_username || p['cognito:username'] || p.email || null;
}
